// ** Modules
import * as React from 'react';

// ** Components
import MegaMenuOverlay from '@/components/common/MegaMenuOverlay';

// ** Utils
import { useGetMegaMenu } from '@/lib/utils/megaMenuDataUtils';

// ** Mappers
import { mapStrapiMegaMenu } from '@/features/ap07/mappers';

// ** Types
import type { MegaMenuOverlayProps } from '@/components/common/MegaMenuOverlay/megaMenuOverlay.types';

type MegaMenuOverlayConnectedProps = Omit<MegaMenuOverlayProps, 'menuView'> & {
	/**
	 * Active locale passed to the GraphQL query (same value the app layout uses
	 * for `useGetNavigation` / `useGetFooter`).
	 */
	locale: string;
	/** Force a cache-bypassing refetch (e.g. right after an SSO login). */
	forceRefetch?: boolean;
	host: string;
};

/**
 * Strapi-connected variant of {@link MegaMenuOverlay}.
 *
 * Fetches the `mega-menu` single type via {@link useGetMegaMenu}, maps the
 * payload through {@link mapStrapiMegaMenu} (falling back to the AP07 mock
 * until the CMS is populated), and delegates rendering to the UI overlay.
 *
 * The component itself stays free of Strapi knowledge — swap back to
 * {@link MegaMenuOverlay} directly if you need a fully offline instance
 * (dev/test pages already do this).
 */
const MegaMenuOverlayConnected = ({ locale, forceRefetch = false, ...overlayProps }: MegaMenuOverlayConnectedProps) => {
	const { data } = useGetMegaMenu(locale, forceRefetch);
	const menuView = React.useMemo(() => mapStrapiMegaMenu(data?.megaMenu), [data?.megaMenu]);

	return <MegaMenuOverlay {...overlayProps} menuView={menuView} />;
};

export default MegaMenuOverlayConnected;
